import React, { useEffect, useState } from 'react';
import {
  Card, Tabs, Table, Button, Space, message, Modal, Form, Input, InputNumber,
  Switch, Select, Tag, Popconfirm, Typography, Descriptions, Drawer, Row, Col,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, MinusCircleOutlined, AppstoreAddOutlined,
} from '@ant-design/icons';
import { apiClient } from 'Utils/api';
import TaxRatesTab from './TaxRatesTab';

const { Title } = Typography;

interface ShippingTier {
  id?: string;
  valueFromCents: number;
  valueToCents: number;
  rateCents: number;
  sortOrder?: number;
}

interface ShippingZone {
  id: string;
  name: string;
  countryCodes: string[] | string;
  baseRateCents: number;
  freeShippingThresholdCents: number | null;
  isActive: boolean;
  tiers?: ShippingTier[];
}

const COMMON_COUNTRIES: { code: string; label: string }[] = [
  { code: 'DE', label: 'Deutschland' },
  { code: 'AT', label: 'Österreich' },
  { code: 'CH', label: 'Schweiz' },
  { code: 'LI', label: 'Liechtenstein' },
  { code: 'FR', label: 'Frankreich' },
  { code: 'IT', label: 'Italien' },
  { code: 'ES', label: 'Spanien' },
  { code: 'NL', label: 'Niederlande' },
  { code: 'BE', label: 'Belgien' },
  { code: 'LU', label: 'Luxemburg' },
  { code: 'PL', label: 'Polen' },
  { code: 'CZ', label: 'Tschechien' },
  { code: 'DK', label: 'Dänemark' },
  { code: 'SE', label: 'Schweden' },
  { code: 'NO', label: 'Norwegen' },
  { code: 'FI', label: 'Finnland' },
  { code: 'GB', label: 'Großbritannien' },
  { code: 'US', label: 'USA' },
  { code: '*', label: 'Welt (alle übrigen Länder)' },
];

const parseCodes = (codes: string[] | string): string[] => {
  if (Array.isArray(codes)) return codes;
  try { return JSON.parse(codes); } catch { return []; }
};

const ShippingZonesTab: React.FC = () => {
  const [zones, setZones] = useState<ShippingZone[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<ShippingZone | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  // Tier-management drawer state
  const [tierZone, setTierZone] = useState<ShippingZone | null>(null);
  const [tierRows, setTierRows] = useState<Array<{ fromEur: number; toEur: number; rateEur: number }>>([]);
  const [savingTiers, setSavingTiers] = useState(false);

  useEffect(() => { fetchZones(); }, []);

  const fetchZones = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/admin/shipping-zones');
      setZones(res.data.shippingZones || []);
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      countryCodes: [],
      baseRateCents: 0,
    });
    setCreating(true);
  };

  const openEdit = (zone: ShippingZone) => {
    setEditing(zone);
    form.setFieldsValue({
      name: zone.name,
      countryCodes: parseCodes(zone.countryCodes),
      baseRateEur: zone.baseRateCents / 100,
      freeShippingThresholdEur: zone.freeShippingThresholdCents
        ? zone.freeShippingThresholdCents / 100
        : null,
      isActive: zone.isActive,
    });
    setCreating(true);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        name: values.name,
        countryCodes: values.countryCodes,
        baseRateCents: Math.round((values.baseRateEur || 0) * 100),
        freeShippingThresholdCents: values.freeShippingThresholdEur
          ? Math.round(values.freeShippingThresholdEur * 100)
          : null,
        isActive: values.isActive,
      };
      if (editing) {
        await apiClient.patch(`/api/admin/shipping-zones/${editing.id}`, payload);
        message.success('Versandzone aktualisiert');
      } else {
        await apiClient.post('/api/admin/shipping-zones', payload);
        message.success('Versandzone angelegt');
      }
      setCreating(false);
      fetchZones();
    } catch (e: any) {
      if (!e.errorFields) {
        message.error(e.response?.data?.message || 'Fehler');
      }
    }
  };

  const toggleActive = async (zone: ShippingZone, isActive: boolean) => {
    try {
      await apiClient.patch(`/api/admin/shipping-zones/${zone.id}`, { isActive });
      fetchZones();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    }
  };

  const remove = async (id: string) => {
    try {
      await apiClient.delete(`/api/admin/shipping-zones/${id}`);
      message.success('Gelöscht');
      fetchZones();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler');
    }
  };

  // --- Tier management ---
  const openTierDrawer = (zone: ShippingZone) => {
    const tiers = (zone.tiers || []).slice().sort((a, b) => a.valueFromCents - b.valueFromCents);
    setTierRows(
      tiers.length > 0
        ? tiers.map((t) => ({
            fromEur: t.valueFromCents / 100,
            toEur: t.valueToCents / 100,
            rateEur: t.rateCents / 100,
          }))
        : [{ fromEur: 0, toEur: 9999.99, rateEur: 12 }],
    );
    setTierZone(zone);
  };

  const updateTierRow = (idx: number, key: 'fromEur' | 'toEur' | 'rateEur', value: number | null) => {
    setTierRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [key]: value || 0 } : r)));
  };

  const addTierRow = () => {
    const last = tierRows[tierRows.length - 1];
    const nextFrom = last ? last.toEur + 0.01 : 0;
    const nextTo = nextFrom + 10000;
    setTierRows((prev) => [...prev, { fromEur: nextFrom, toEur: nextTo, rateEur: 0 }]);
  };

  const removeTierRow = (idx: number) => setTierRows((prev) => prev.filter((_, i) => i !== idx));

  const saveTiers = async () => {
    if (!tierZone) return;
    setSavingTiers(true);
    try {
      const payload = {
        tiers: tierRows.map((r, i) => ({
          valueFromCents: Math.round(r.fromEur * 100),
          valueToCents: Math.round(r.toEur * 100),
          rateCents: Math.round(r.rateEur * 100),
          sortOrder: i,
        })),
      };
      await apiClient.put(`/api/admin/shipping-zones/${tierZone.id}/tiers`, payload);
      message.success('Versand-Tabelle gespeichert');
      setTierZone(null);
      fetchZones();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setSavingTiers(false);
    }
  };

  const fmtEur = (cents: number) => (cents / 100).toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    {
      title: 'Länder',
      dataIndex: 'countryCodes',
      key: 'countryCodes',
      render: (v: any) => {
        const arr = parseCodes(v);
        return (
          <Space wrap>
            {arr.map((c) => <Tag key={c}>{c}</Tag>)}
          </Space>
        );
      },
    },
    {
      title: 'Versand-Tabelle (Bestellwert → Preis)',
      key: 'tiers',
      render: (_: any, r: ShippingZone) => {
        const tiers = (r.tiers || []).slice().sort((a, b) => a.valueFromCents - b.valueFromCents);
        if (tiers.length === 0) {
          return (
            <Tag color="orange" style={{ cursor: 'pointer' }} onClick={() => openTierDrawer(r)}>
              Keine Stufen – jetzt anlegen
            </Tag>
          );
        }
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {tiers.map((t, i) => (
              <span key={i} style={{ fontSize: 12 }}>
                <strong>{fmtEur(t.valueFromCents)}–{fmtEur(t.valueToCents)} €</strong>
                {' → '}
                <span style={{ color: '#b8964a' }}>{fmtEur(t.rateCents)} €</span>
              </span>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Aktiv',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (active: boolean, r: ShippingZone) => (
        <Switch checked={active} onChange={(v) => toggleActive(r, v)} />
      ),
    },
    {
      title: 'Aktionen',
      key: 'actions',
      width: 250,
      render: (_: any, r: ShippingZone) => (
        <Space>
          <Button
            icon={<AppstoreAddOutlined />}
            size="small"
            type="primary"
            onClick={() => openTierDrawer(r)}
            data-testid={`zone-tiers-${r.id}`}
          >
            Tabelle
          </Button>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(r)}>
            Bearbeiten
          </Button>
          <Popconfirm title="Versandzone löschen?" onConfirm={() => remove(r.id)} okText="Löschen" cancelText="Abbrechen">
            <Button danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Versandzone anlegen
        </Button>
      </div>
      <Table
        rowKey="id"
        dataSource={zones}
        columns={columns}
        loading={loading}
        pagination={false}
      />

      <Modal
        title={editing ? 'Versandzone bearbeiten' : 'Versandzone anlegen'}
        open={creating}
        onCancel={() => setCreating(false)}
        onOk={submit}
        okText="Speichern"
        cancelText="Abbrechen"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Bitte Name angeben' }]}>
            <Input placeholder="z. B. EU" />
          </Form.Item>
          <Form.Item
            label="Länder (ISO-2 Codes oder * für Welt)"
            name="countryCodes"
            rules={[{ required: true, message: 'Mindestens ein Land wählen' }]}
            extra={
              <span>
                Du kannst auch eigene Codes eintippen und mit Enter bestätigen. * = alle übrigen Länder.
              </span>
            }
          >
            <Select
              mode="tags"
              placeholder="DE, AT, CH …"
              options={COMMON_COUNTRIES.map((c) => ({ value: c.code, label: `${c.code} – ${c.label}` }))}
            />
          </Form.Item>
          <Form.Item
            label="Fallback-Grundgebühr (€)"
            name="baseRateEur"
            extra="Wird nur genutzt, wenn keine passende Stufe in der Versand-Tabelle matcht."
            rules={[{ required: true, message: 'Pflichtfeld' }]}
          >
            <InputNumber min={0} step={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Aktiv" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={`Versand-Tabelle: ${tierZone?.name || ''}`}
        placement="right"
        width={680}
        open={!!tierZone}
        onClose={() => setTierZone(null)}
        extra={
          <Space>
            <Button onClick={() => setTierZone(null)}>Abbrechen</Button>
            <Button type="primary" loading={savingTiers} onClick={saveTiers} data-testid="save-tiers-btn">
              Speichern
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 16, color: '#555' }}>
          Definiere Bestellwert-Bereiche und den jeweiligen Versandpreis (in Euro). Bereiche dürfen sich
          <strong> nicht überlappen</strong>. Wird kein passender Bereich gefunden, greift die Fallback-Grundgebühr.
        </div>

        <Table
          rowKey={(_r, i) => String(i)}
          dataSource={tierRows}
          pagination={false}
          size="small"
          columns={[
            {
              title: 'Bestellwert von (€)',
              dataIndex: 'fromEur',
              key: 'fromEur',
              render: (v: number, _row, i) => (
                <InputNumber
                  value={v}
                  min={0}
                  step={100}
                  style={{ width: '100%' }}
                  onChange={(val) => updateTierRow(i, 'fromEur', val as number)}
                  data-testid={`tier-from-${i}`}
                />
              ),
            },
            {
              title: 'bis (€)',
              dataIndex: 'toEur',
              key: 'toEur',
              render: (v: number, _row, i) => (
                <InputNumber
                  value={v}
                  min={0}
                  step={100}
                  style={{ width: '100%' }}
                  onChange={(val) => updateTierRow(i, 'toEur', val as number)}
                  data-testid={`tier-to-${i}`}
                />
              ),
            },
            {
              title: 'Versandpreis (€)',
              dataIndex: 'rateEur',
              key: 'rateEur',
              render: (v: number, _row, i) => (
                <InputNumber
                  value={v}
                  min={0}
                  step={1}
                  style={{ width: '100%' }}
                  onChange={(val) => updateTierRow(i, 'rateEur', val as number)}
                  data-testid={`tier-rate-${i}`}
                />
              ),
            },
            {
              title: '',
              key: 'remove',
              width: 50,
              render: (_v, _r, i) => (
                <Button
                  type="text"
                  icon={<MinusCircleOutlined />}
                  danger
                  onClick={() => removeTierRow(i)}
                  data-testid={`tier-remove-${i}`}
                />
              ),
            },
          ]}
        />

        <Button
          icon={<PlusOutlined />}
          onClick={addTierRow}
          style={{ marginTop: 12 }}
          data-testid="add-tier-btn"
        >
          Stufe hinzufügen
        </Button>
      </Drawer>
    </>
  );
};

const SystemTab: React.FC = () => (
  <Card>
    <Descriptions title="System-Informationen" bordered column={1}>
      <Descriptions.Item label="Version">1.0.0</Descriptions.Item>
      <Descriptions.Item label="Umgebung">Production</Descriptions.Item>
      <Descriptions.Item label="Datenbank">MariaDB</Descriptions.Item>
      <Descriptions.Item label="Mailer">
        <Tag>Stub – wird vom Betreiber konfiguriert</Tag>
      </Descriptions.Item>
    </Descriptions>
  </Card>
);

interface PickupLocation {
  enabled: boolean;
  companyName: string;
  street: string;
  postalCode: string;
  city: string;
  countryCode: string;
  contactHint: { de: string; en: string };
}

const DEFAULT_PICKUP: PickupLocation = {
  enabled: false,
  companyName: '',
  street: '',
  postalCode: '',
  city: '',
  countryCode: 'DE',
  contactHint: {
    de: 'Ein persönlicher Abholtermin wird per E-Mail oder Telefon mit Ihnen abgestimmt.',
    en: 'A personal pickup appointment will be arranged with you by email or phone.',
  },
};

const PickupLocationTab: React.FC = () => {
  const [pickup, setPickup] = useState<PickupLocation>(DEFAULT_PICKUP);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/api/pickup-location');
      if (res.data?.pickup) {
        setPickup({ ...DEFAULT_PICKUP, ...res.data.pickup });
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = (key: keyof PickupLocation, value: any) => {
    setPickup((prev) => ({ ...prev, [key]: value }));
  };

  const updateHint = (lang: 'de' | 'en', value: string) => {
    setPickup((prev) => ({
      ...prev,
      contactHint: { ...prev.contactHint, [lang]: value },
    }));
  };

  const save = async () => {
    if (pickup.enabled) {
      if (!pickup.companyName.trim() || !pickup.street.trim() || !pickup.postalCode.trim() || !pickup.city.trim()) {
        message.error('Firma, Straße, PLZ und Stadt sind bei aktivierter Abholung Pflichtfelder.');
        return;
      }
    }
    setSaving(true);
    try {
      await apiClient.put('/api/admin/settings/pickup-location', pickup);
      message.success('Abholadresse gespeichert');
      load();
    } catch (e: any) {
      message.error(e.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card loading={loading}>
      <div style={{ marginBottom: 16, color: '#555' }}>
        Aktiviere die Abholung im Showroom, damit Kunden im Checkout zwischen <strong>Versand</strong> und
        <strong> Abholung</strong> wählen können. Bei Abholung wird kein Versand berechnet und es wird
        der hier hinterlegte Hinweis angezeigt.
      </div>

      <Form layout="vertical" style={{ maxWidth: 720 }}>
        <Form.Item label="Abholung aktiviert">
          <Switch
            checked={pickup.enabled}
            onChange={(v) => update('enabled', v)}
            data-testid="pickup-enabled-switch"
          />
        </Form.Item>

        <Form.Item label="Firmenname / Showroom" required>
          <Input
            value={pickup.companyName}
            onChange={(e) => update('companyName', e.target.value)}
            placeholder="z. B. MR Luxury Watches GmbH"
            data-testid="pickup-companyName-input"
          />
        </Form.Item>

        <Form.Item label="Straße & Hausnummer" required>
          <Input
            value={pickup.street}
            onChange={(e) => update('street', e.target.value)}
            placeholder="z. B. Musterstraße 12"
            data-testid="pickup-street-input"
          />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item label="PLZ" required>
              <Input
                value={pickup.postalCode}
                onChange={(e) => update('postalCode', e.target.value)}
                data-testid="pickup-postalCode-input"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item label="Stadt" required>
              <Input
                value={pickup.city}
                onChange={(e) => update('city', e.target.value)}
                data-testid="pickup-city-input"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Land">
              <Select
                value={pickup.countryCode}
                onChange={(v) => update('countryCode', v)}
                options={COMMON_COUNTRIES
                  .filter((c) => c.code !== '*')
                  .map((c) => ({ value: c.code, label: c.code }))}
                data-testid="pickup-country-select"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Hinweistext für Kunden – Deutsch"
          extra="Wird im Checkout angezeigt, wenn der Kunde Abholung wählt."
        >
          <Input.TextArea
            rows={3}
            value={pickup.contactHint.de}
            onChange={(e) => updateHint('de', e.target.value)}
            data-testid="pickup-hint-de-textarea"
          />
        </Form.Item>

        <Form.Item label="Hinweistext für Kunden – English">
          <Input.TextArea
            rows={3}
            value={pickup.contactHint.en}
            onChange={(e) => updateHint('en', e.target.value)}
            data-testid="pickup-hint-en-textarea"
          />
        </Form.Item>

        <Button
          type="primary"
          loading={saving}
          onClick={save}
          data-testid="pickup-save-btn"
        >
          Abholadresse speichern
        </Button>
      </Form>
    </Card>
  );
};

const SettingsPage: React.FC = () => {
  return (
    <div>
      <Title level={3}>Einstellungen</Title>
      <Tabs
        items={[
          { key: 'shipping', label: 'Versandzonen', children: <ShippingZonesTab /> },
          { key: 'tax', label: 'Steuersätze', children: <TaxRatesTab /> },
          { key: 'pickup', label: 'Abholung', children: <PickupLocationTab /> },
          { key: 'system', label: 'System', children: <SystemTab /> },
        ]}
      />
    </div>
  );
};

export default SettingsPage;
